import {
  createContext,
  useContext,
  useMemo,
  useState,
  useCallback,
} from "react";
import { useGetMyQuickLoans } from "../hooks/scenario.hooks";

const SELECTED_LOAN_ID_KEY = "loan-application-selected-loan-id";

const getStoredLoanId = () => {
  if (typeof window === "undefined") return null;

  return sessionStorage.getItem(SELECTED_LOAN_ID_KEY) || null;
};

const setStoredLoanId = (loanId) => {
  if (typeof window === "undefined") return;
  if (loanId) {
    sessionStorage.setItem(SELECTED_LOAN_ID_KEY, String(loanId));
  } else {
    sessionStorage.removeItem(SELECTED_LOAN_ID_KEY);
  }
};

const SelectedDraftLoanContext = createContext({
  selectedDraftId: null,
  setSelectedDraftId: () => Promise.resolve(),
  draftList: [],
  isLoadingDraftList: false,
  refetchDraftList: () => Promise.resolve(),
});

export const useSelectedDraftLoan = () => {
  return useContext(SelectedDraftLoanContext);
};

export const SelectedDraftLoanProvider = ({ children }) => {
  const [selectedDraftId, setSelectedDraftIdState] = useState(() =>
    getStoredLoanId()
  );

  const {
    data: draftList = [],
    isLoading: isLoadingDraftList,
    refetch: refetchDraftList,
  } = useGetMyQuickLoans({
    retry: (failureCount, error) => {
      if (error?.status === 401) return false;
      return failureCount < 2;
    },
    onSuccess: (nextList) => {
      // If the selected loan no longer exists (e.g. deleted elsewhere), clear it (and storage).
      setSelectedDraftIdState((prevSelectedId) => {
        if (!prevSelectedId) return prevSelectedId;
        const stillExists = (nextList || []).some(
          (loan) => String(loan?.id) === String(prevSelectedId)
        );
        if (stillExists) return prevSelectedId;
        setStoredLoanId(null);
        return null;
      });
    },
  });

  const setSelectedDraftId = useCallback(
    (loanId) => {
      setSelectedDraftIdState(loanId);
      setStoredLoanId(loanId);

      // Re-fetch the loan list when user selects a loan, to ensure it still exists.
      if (loanId) {
        return refetchDraftList().catch(() => undefined);
      }
      return Promise.resolve();
    },
    [refetchDraftList]
  );

  const value = useMemo(
    () => ({
      selectedDraftId,
      setSelectedDraftId,
      draftList,
      isLoadingDraftList,
      refetchDraftList,
    }),
    [
      selectedDraftId,
      setSelectedDraftId,
      draftList,
      isLoadingDraftList,
      refetchDraftList,
    ]
  );

  return (
    <SelectedDraftLoanContext.Provider value={value}>
      {children}
    </SelectedDraftLoanContext.Provider>
  );
};

export default SelectedDraftLoanContext;
